import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Upload,
  Trash2,
  Copy,
  Check,
  Loader2,
  Image,
  FileText,
  Film,
  HardDrive
} from 'lucide-react';

const MediaPage = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [maxSize, setMaxSize] = useState(500 * 1024 * 1024);
  const [copiedId, setCopiedId] = useState(null);

  const fetchMedia = async () => {
    try {
      const res = await api.get('/media');
      setFiles(res.data.files || []);
      setTotalSize(res.data.total_size || 0);
      setMaxSize(res.data.max_size || 500 * 1024 * 1024);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMedia(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchMedia();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Upload failed';
      alert(msg);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file permanently?')) return;
    try {
      await api.delete(`/media/${fileId}`);
      fetchMedia();
    } catch (e) {
      alert('Failed to delete');
    }
  };

  const copyUrl = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(url);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getIcon = (type) => {
    if (type === 'image') return <Image size={20} className="text-blue-500" />;
    if (type === 'video') return <Film size={20} className="text-purple-500" />;
    if (type === 'pdf') return <FileText size={20} className="text-red-500" />;
    return <FileText size={20} className="text-gray-500" />;
  };

  const usedPercent = maxSize > 0 ? (totalSize / maxSize) * 100 : 0;

  return (
    <div className="space-y-6" data-testid="media-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Media Library</h1>
          <p className="text-gray-500 mt-1">Upload and manage media files for WhatsApp templates</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <HardDrive size={14} />
              <span>{formatSize(totalSize)} / {formatSize(maxSize)}</span>
            </div>
            <div className="w-32 bg-gray-200 rounded-full h-1.5 mt-1">
              <div
                className={`h-1.5 rounded-full ${usedPercent > 80 ? 'bg-red-500' : 'bg-[#4F46E5]'}`}
                style={{ width: `${Math.min(usedPercent, 100)}%` }}
              />
            </div>
          </div>
          <label className="cursor-pointer">
            <input type="file" className="hidden" onChange={handleUpload} accept="image/*,video/*,.pdf" />
            <Button asChild disabled={uploading}>
              <span className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">
                {uploading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Upload size={16} className="mr-2" />}
                Upload File
              </span>
            </Button>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
        </div>
      ) : files.length === 0 ? (
        <Card className="p-16 text-center bg-white border border-gray-100">
          <Image className="mx-auto text-gray-300 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No media files yet</h3>
          <p className="text-gray-500 mb-4">Upload images, videos, or PDFs to use in your WhatsApp templates</p>
          <label className="cursor-pointer inline-block">
            <input type="file" className="hidden" onChange={handleUpload} accept="image/*,video/*,.pdf" />
            <Button asChild>
              <span className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">
                <Upload size={16} className="mr-2" /> Upload First File
              </span>
            </Button>
          </label>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {files.map((file) => (
            <Card key={file.id} className="bg-white border border-gray-100 overflow-hidden group hover:shadow-md transition-shadow" data-testid={`media-file-${file.id}`}>
              {/* Preview */}
              <div className="aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                {file.file_type === 'image' ? (
                  <img src={file.url} alt={file.original_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    {getIcon(file.file_type)}
                    <p className="text-xs text-gray-400 mt-2">{file.file_type.toUpperCase()}</p>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-2">
                <p className="text-xs font-medium text-gray-800 truncate" title={file.original_name}>
                  {file.original_name}
                </p>
                <p className="text-xs text-gray-400">{formatSize(file.size)}</p>
              </div>

              {/* Actions */}
              <div className="px-2 pb-2 flex gap-1">
                <button
                  onClick={() => copyUrl(file.url)}
                  className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-gray-600 hover:text-[#4F46E5] hover:bg-[#4F46E5]/5 rounded transition-colors"
                  title="Copy URL"
                  data-testid={`copy-url-${file.id}`}
                >
                  {copiedId === file.url ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                  {copiedId === file.url ? 'Copied' : 'Copy URL'}
                </button>
                <button
                  onClick={() => handleDelete(file.id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Delete"
                  data-testid={`delete-media-${file.id}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaPage;
